import type { Identifier } from "ra-core";

import type {
  CorretajeColegaFoundBy,
  CorretajeCommissionScheme,
  CorretajePropertyOwner,
  CorretajeSplitResult,
  PropertyOption,
} from "../types";
import { computeCorretajeSplit } from "./corretajeSplit";

/**
 * Almikia Epic EC (TASK-032): pure rules of a corretaje sale administration,
 * shared by the FakeRest provider and the "Administracion de la venta" form.
 * They mirror `private.assert_corretaje_sale_inputs`, `sync_corretaje_participants`
 * and `corretaje_split_for_operation` (supabase/schemas/02_functions.sql): same
 * checks in the same order and the same Spanish messages. The database stays
 * the authority; the screens only use these to guide and to preview the split.
 */

/** Values the sale administration form edits (and `save_corretaje_sale` receives). */
export type CorretajeSaleInput = {
  shared_with_colega: boolean;
  colega_id: Identifier | null;
  colega_found_by: CorretajeColegaFoundBy | null;
  property_owner: CorretajePropertyOwner;
  /** Total commission C; null while it is still empty. */
  commission_total: number | null;
};

export const SALE_SHARED_REQUIRED_MESSAGE =
  "Indica si la venta se comparte con un colega";
export const SALE_OWNER_REQUIRED_MESSAGE =
  "Indica si la propiedad es de nuestro inventario o del colega";
export const SALE_FOUND_BY_INVALID_MESSAGE =
  "Quién encontró al colega no es válido";
export const SALE_COLEGA_REQUIRED_MESSAGE =
  "Elige el colega con quien se comparte la venta";
export const SALE_FOUND_BY_REQUIRED_MESSAGE = "Indica quién encontró al colega";
export const SALE_NOT_SHARED_MESSAGE =
  "Sin colega no se indica el colega ni quién lo encontró";
export const SALE_COLEGA_PROPERTY_MESSAGE =
  "Una propiedad del colega solo se vende compartida con el colega";
export const SALE_COMMISSION_INVALID_MESSAGE =
  "La comisión total debe ser un monto igual o mayor a cero";
export const SALE_CLAVE_REQUIRED_MESSAGE =
  "La vendedora necesita su clave antes de guardar la venta";
export const SALE_COMMISSION_REQUIRED_MESSAGE =
  "Captura la comisión total antes de solicitar el VoBo";

const FOUND_BY_VALUES: readonly string[] = [
  "asesora_proactiva",
  "lead_manager",
  "direccion_corretaje",
];

/** The loosely-typed facts the validator accepts (a form may leave them empty). */
export type CorretajeSaleFacts = {
  shared_with_colega?: boolean | null;
  colega_id?: Identifier | null;
  colega_found_by?: string | null;
  property_owner?: string | null;
  commission_total?: number | null;
};

/**
 * Returns the first message `assert_corretaje_sale_inputs` would raise, or null
 * when the facts are valid. `forVobo` adds the "commission required" check.
 */
export const validateCorretajeSale = (
  facts: CorretajeSaleFacts,
  options: { vendedoraHasClave: boolean; forVobo: boolean },
): string | null => {
  if (facts.shared_with_colega == null) return SALE_SHARED_REQUIRED_MESSAGE;
  if (facts.property_owner !== "almikia" && facts.property_owner !== "colega") {
    return SALE_OWNER_REQUIRED_MESSAGE;
  }
  if (
    facts.colega_found_by != null &&
    !FOUND_BY_VALUES.includes(facts.colega_found_by)
  ) {
    return SALE_FOUND_BY_INVALID_MESSAGE;
  }
  if (facts.shared_with_colega) {
    if (facts.colega_id == null) return SALE_COLEGA_REQUIRED_MESSAGE;
    if (facts.colega_found_by == null) return SALE_FOUND_BY_REQUIRED_MESSAGE;
  } else if (facts.colega_id != null || facts.colega_found_by != null) {
    return SALE_NOT_SHARED_MESSAGE;
  }
  if (facts.property_owner === "colega" && !facts.shared_with_colega) {
    return SALE_COLEGA_PROPERTY_MESSAGE;
  }
  if (facts.commission_total != null && facts.commission_total < 0) {
    return SALE_COMMISSION_INVALID_MESSAGE;
  }
  if (!options.vendedoraHasClave) return SALE_CLAVE_REQUIRED_MESSAGE;
  if (options.forVobo && facts.commission_total == null) {
    return SALE_COMMISSION_REQUIRED_MESSAGE;
  }
  return null;
};

export type MissingSaleField =
  | "shared"
  | "colega"
  | "found_by"
  | "vendedora_clave"
  | "commission_total";

/** What is still missing before the VoBo can be requested (for the "listo / falta algo" semaforo). */
export const listMissingSaleFields = (
  facts: CorretajeSaleFacts,
  vendedoraHasClave: boolean,
): MissingSaleField[] => {
  const missing: MissingSaleField[] = [];
  if (facts.shared_with_colega == null) missing.push("shared");
  if (facts.shared_with_colega) {
    if (facts.colega_id == null) missing.push("colega");
    if (facts.colega_found_by == null) missing.push("found_by");
  }
  if (!vendedoraHasClave) missing.push("vendedora_clave");
  if (facts.commission_total == null) missing.push("commission_total");
  return missing;
};

export type Opcionadora = { sales_id: Identifier | null; is_amk: boolean };

/**
 * Who is the opcionadora: the Almikia vendedora when the property is the
 * colega's, otherwise the option's responsable (an advisor or AMK).
 */
export const deriveOpcionadora = (
  propertyOwner: CorretajePropertyOwner,
  option: Pick<PropertyOption, "responsable_sales_id" | "responsable_is_amk">,
  vendedoraSalesId: Identifier,
): Opcionadora =>
  propertyOwner === "colega"
    ? { sales_id: vendedoraSalesId, is_amk: false }
    : {
        sales_id: option.responsable_sales_id ?? null,
        is_amk: Boolean(option.responsable_is_amk),
      };

export type SaleSplitOutcome =
  | { ok: true; split: CorretajeSplitResult }
  | { ok: false; message: string };

/**
 * The live split of a sale (mirror of `corretaje_split_for_operation`), or the
 * reason it cannot be shown yet. Never ranks or compares advisors.
 */
export const computeSaleSplit = (
  facts: CorretajeSaleFacts,
  option: Pick<PropertyOption, "responsable_sales_id" | "responsable_is_amk">,
  vendedoraSalesId: Identifier,
  schemes: CorretajeCommissionScheme[],
  on: string,
): SaleSplitOutcome => {
  if (facts.shared_with_colega == null) {
    return { ok: false, message: SALE_SHARED_REQUIRED_MESSAGE };
  }
  if (facts.property_owner !== "almikia" && facts.property_owner !== "colega") {
    return { ok: false, message: SALE_OWNER_REQUIRED_MESSAGE };
  }
  if (facts.commission_total == null || Number.isNaN(facts.commission_total)) {
    return { ok: false, message: SALE_COMMISSION_REQUIRED_MESSAGE };
  }
  const opcionadora = deriveOpcionadora(
    facts.property_owner,
    option,
    vendedoraSalesId,
  );
  const isAmk = facts.property_owner !== "colega" && opcionadora.is_amk;
  const sameAdvisor =
    !isAmk &&
    (facts.property_owner === "colega" ||
      String(opcionadora.sales_id) === String(vendedoraSalesId));
  try {
    const split = computeCorretajeSplit(
      {
        sharedWithColega: facts.shared_with_colega,
        colegaFoundBy: (facts.colega_found_by ??
          null) as CorretajeColegaFoundBy | null,
        propertyOwner: facts.property_owner,
        opcionadoraKind: isAmk ? "amk" : "advisor",
        sameAdvisor,
        total: facts.commission_total,
        on,
      },
      schemes,
    );
    return { ok: true, split };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : String(error),
    };
  }
};
